/**
 * Knowledge Base API client
 */
import { apiClient, ApiClient, ApiError } from './client';
import type { OntologyGenerationJob } from '@shared/types/ontology';
import { env } from '@/config/env';
import { authHeader } from '@/lib/auth/token-store';
import type { KnowledgeBase, KBDocument, KnowledgeUnit, KnowledgeUnitSummary, KnowledgeUnitExtraction } from '@shared/types/knowledge-base';


// Use a longer timeout for ontology generation from documents (5 min) since LLM calls can be slow
const generationApiClient = new ApiClient({ timeout: 5 * 60 * 1000 });

export interface CreateKnowledgeBaseDto {
  name: string;
  description?: string;
  type?: 'unstructured' | 'structured' | 'live' | 'stock';
}

export interface UpdateKnowledgeBaseDto {
  name?: string;
  description?: string;
}

export const knowledgeBaseApi = {
  // ─── Knowledge Bases ─────────────────────────────────────────────────
  list: () => apiClient.get<KnowledgeBase[]>('/api/knowledge-bases'),

  get: (id: string) => apiClient.get<KnowledgeBase>(`/api/knowledge-bases/${id}`),

  create: (data: CreateKnowledgeBaseDto) =>
    apiClient.post<KnowledgeBase>('/api/knowledge-bases', data),

  update: (id: string, data: UpdateKnowledgeBaseDto) =>
    apiClient.put<KnowledgeBase>(`/api/knowledge-bases/${id}`, data),

  delete: (id: string) => apiClient.delete<void>(`/api/knowledge-bases/${id}`),

  // ─── Documents ───────────────────────────────────────────────────────
  // `signal` is for the verification reads in useResilientMutation (upload, delete, retry):
  // they need a much shorter deadline than env.apiTimeout, and the api client honours a
  // caller-supplied signal instead of its own 30s controller.
  listDocuments: (kbId: string, signal?: AbortSignal) =>
    apiClient.get<KBDocument[]>(`/api/knowledge-bases/${kbId}/knowledge-objects`, { signal }),

  /**
   * Import documents (copy, with their units + embeddings) from another KB into
   * `targetKbId`. Pass `documentIds` for a selection, or `all: true` for the
   * whole source collection. Returns how many were imported vs skipped (dedup).
   */
  importDocuments: (
    targetKbId: string,
    body: { sourceKbId: string; documentIds?: string[]; all?: boolean }
  ) =>
    apiClient.post<{ imported: number; skipped: number; units: number }>(
      `/api/knowledge-bases/${targetKbId}/import-documents`,
      body
    ),

  getDocument: (id: string) =>
    apiClient.get<KBDocument>(`/api/knowledge-objects/${id}`),

  getDocumentDownloadUrl: (id: string) =>
    apiClient.get<{ url: string }>(`/api/knowledge-objects/${id}/download`),

  getDocumentMarkdown: (id: string) =>
    apiClient.get<{ content: string }>(`/api/knowledge-objects/${id}/markdown`),

  /**
   * Ontology concepts grounded in this document (across explorations). Powers
   * the "Concepts in this document" panel. Empty until the doc has been included
   * in an exploration that grounded it.
   */
  getKnowledgeObjectConcepts: (knowledgeObjectId: string) =>
    apiClient.get<
      Array<{
        ontology_node_id: string;
        label: string;
        grounding_count: number;
        best_score: number;
        exploration_count: number;
      }>
    >(`/api/knowledge-objects/${knowledgeObjectId}/concepts`),

  /**
   * List the knowledge units (chunks) of a knowledge object. Embeddings are
   * never returned over the wire — they are 768-dim vectors and only relevant
   * server-side for similarity search.
   */
  listKnowledgeUnits: (knowledgeObjectId: string) =>
    apiClient.get<KnowledgeUnit[]>(
      `/api/knowledge-objects/${knowledgeObjectId}/knowledge-units`
    ),

  /**
   * Re-run the chunking + embedding pipeline for a knowledge object whose
   * conversion has already completed. Returns immediately; the chunker runs
   * fire-and-forget and the unit count will update on the next poll.
   */
  rechunkKnowledgeObject: (knowledgeObjectId: string) =>
    apiClient.post<{ ok: true }>(
      `/api/knowledge-objects/${knowledgeObjectId}/rechunk`
    ),

  /**
   * Re-run ingestion for a document that failed or is stuck — the server resets
   * the row to `pending` and re-fires the COS pipeline (reconvert + embed).
   * Returns immediately; the status updates on the next poll.
   */
  retryKnowledgeObject: (knowledgeObjectId: string) =>
    apiClient.post<{ ok: true }>(
      `/api/knowledge-objects/${knowledgeObjectId}/retry`
    ),

  // Rename a document's user-facing label (A15). Pass null/'' to clear it
  // (falls back to filename). Slug/COS path/filename are unchanged server-side.
  renameKnowledgeObject: (knowledgeObjectId: string, displayName: string | null) =>
    apiClient.patch<KBDocument>(`/api/knowledge-objects/${knowledgeObjectId}`, {
      display_name: displayName,
    }),

  // "Rename with AI" — ask the backend to suggest a human-readable display
  // name derived from the document's own knowledge units. Never persists
  // anything; the caller fills the Rename dialog input with the result.
  suggestDisplayName: (knowledgeObjectId: string) =>
    apiClient.post<{ suggested_display_name: string }>(
      `/api/knowledge-objects/${knowledgeObjectId}/suggest-display-name`
    ),

  /**
   * Queue L2/L3 for a document. Returns 202: the work runs in the layers worker (root BACKLOG §27) and is
   * NOT done when this resolves, so there are no counts to return. The previous type promised
   * `summaries_created` / `extractions_created`, which the inline implementation produced and this one
   * cannot: a type that describes a response nobody sends is worse than no type.
   */
  processLayers: (knowledgeObjectId: string) =>
    apiClient.post<{
      knowledge_object_id: string;
      status: 'queued';
      force: boolean;
    }>(`/api/knowledge-objects/${knowledgeObjectId}/process-layers`),

  /**
   * Fetch L2 (summaries) and L3 (extractions) for all units of a knowledge
   * object in a single round-trip.
   */
  listKnowledgeUnitLayers: (knowledgeObjectId: string) =>
    apiClient.get<{ summaries: KnowledgeUnitSummary[]; extractions: KnowledgeUnitExtraction[] }>(
      `/api/knowledge-objects/${knowledgeObjectId}/knowledge-unit-layers`
    ),

  deleteDocument: (id: string) =>
    apiClient.delete<void>(`/api/knowledge-objects/${id}`),


  /**
   * Generate an ontology from selected KB documents.
   * Uses an extended timeout because LLM generation can take several minutes.
   */
  generateFromDocuments: (documentIds: string[], nodeCount: number, focus?: string) =>
    generationApiClient.post<any>('/api/ontologies/generate-from-documents', { documentIds, nodeCount, focus }),

  getOntologyGenerationJob: (jobId: string) =>
    apiClient.get<OntologyGenerationJob>(`/api/ontologies/generation-jobs/${jobId}`),

  /**
   * Upload a document — uses FormData (not JSON),
   * so we bypass the standard apiClient JSON serialization.
   */
  uploadDocument: async (kbId: string, file: File): Promise<KBDocument> => {
    const formData = new FormData();
    formData.append('file', file);

    // In-memory bearer (never storage) — see lib/auth/token-store.ts.
    const headers: Record<string, string> = { ...authHeader() };

    const baseUrl = env.apiUrl || '';
    const resp = await fetch(`${baseUrl}/api/knowledge-bases/${kbId}/knowledge-objects`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const json = await resp.json();
    if (!resp.ok || !json.success) {
      // ApiError rather than Error, and with the status, so the failure can be classified like
      // any other. As a bare Error a 502 from the proxy was indistinguishable from a rejected
      // file type, which meant a lost upload response could never be verified.
      throw new ApiError(
        json.error?.code || 'UPLOAD_FAILED',
        json.error?.message || json.error || 'Upload failed',
        resp.status,
      );
    }
    return json.data as KBDocument;
  },
};

// Made with Bob
